/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {
  Comparator,
  NetworkConnectionStatus,
  NetworkStatus,
  Observables,
  Observer
} from './types';

/**
 * The `ConnectionStatus` API provides a singleton instance which centralizes observations
 * of the network conditions under which applications are operating, primarily, `online`
 * and `offline` status changes.
 *
 * While clients can easily observe and receive realtime notifications of status changes
 * via the `ConnectionStatus` API, generally speaking, this module is typically utilized by
 * higher-level components which are then used directly by specific application Features.
 *
 * ```typescript
 *   import getConnection, {NetworkStatus} from '@ion/core/src/network';
 *
 *   // add offline and online observers
 *   const connection: NetworkStatus = getConnection(onDisconnect, onConnected);
 *
 *   // or via addObservers
 *   const instance: NetworkStatus = getConnection();
 *   connection.addObservers(onDisconnect, onConnected);
 *   // or ...
 *   getConnection().addObservers(onDisconnect, onConnected);
 *
 *   ...
 *   // add an online observer ...
 *   connection.addOnlineObserver(onReConnected);
 *   connection.hasOnlineObserver(onReConnected); // true
 *
 *   ...
 *   // add an offline observer ...
 *   connection.addOfflineObserver(onConnectionDropped);
 *   connection.hasOfflineObserver(onConnectionDropped); // true
 *
 *   // add a single observer for both offline and online events ...
 *   connection.addObservers(onChange, null, true);
 *
 *   ...
 *   // remove an online observer ...
 *   connection.removeOnlineObserver(onReConnected);
 *   connection.hasOnlineObserver(onReConnected); // false
 *
 *   ...
 *   // remove an offline observer ...
 *   connection.removeOfflineObserver(onConnectionDropped);
 *   connection.hasOfflineObserver(onConnectionDropped); // false
 *
 *   // determine if the application is currently online or offline ...
 *   connection.isConnected; // true if online, otherwise, false
 * ```
 *
 */
class ConnectionStatus implements NetworkStatus {
  /**
   * Provides a reference to the current network getConnection status, `true` when online,
   * otherwise false when `offline`.
   */
  private _connected: boolean;

  /**
   * Provides a reference to the Observable collection of registered online and offline
   * event observers.
   */
  private _observers: Observables;

  /**
   * @private
   */
  constructor() {
    this._connected = window.navigator.onLine;
    this._observers = {offline: [], online: []};
    window.addEventListener(NetworkConnectionStatus.offline, this.onDisconnected);
    window.addEventListener(NetworkConnectionStatus.online, this.onConnected);
  }

  /**
   * @see {@link NetworkStatus.isConnected}
   */
  get isConnected(): boolean {
    return this._connected;
  }

  /**
   * @see {@link NetworkStatus.addObservers}
   */
  addObservers(onDisconnect: Observer, onConnect: Observer, observeAll = false) {
    if (onDisconnect) {
      this.addOfflineObserver(onDisconnect);
    }
    if (onConnect || observeAll) {
      this.addOnlineObserver(observeAll ? onDisconnect : onConnect);
    }
  }

  /**
   * @see {@link NetworkStatus.addOnlineObserver}
   */
  addOnlineObserver(observer: Observer) {
    if (observer) {
      this.addObserver(observer, NetworkConnectionStatus.online);
    }
  }

  /**
   * @see {@link NetworkStatus.addOfflineObserver}
   */
  addOfflineObserver(observer: Observer) {
    if (observer) {
      this.addObserver(observer, NetworkConnectionStatus.offline);
    }
  }

  /**
   * @see {@link NetworkStatus.removeOnlineObserver}
   */
  removeOnlineObserver(observer: Observer) {
    if (observer) {
      this.removeObserver(observer, NetworkConnectionStatus.online);
    }
  }

  /**
   * @see {@link NetworkStatus.removeOfflineObserver}
   */
  removeOfflineObserver(observer: Observer) {
    if (observer) {
      this.removeObserver(observer, NetworkConnectionStatus.offline);
    }
  }

  /**
   * @see {@link NetworkStatus.hasOnlineObserver}
   */
  hasOnlineObserver(observer: Observer): boolean {
    return this.hasObserver(observer, NetworkConnectionStatus.online);
  }

  /**
   * @see {@link NetworkStatus.hasOfflineObserver}
   */
  hasOfflineObserver(observer: Observer): boolean {
    return this.hasObserver(observer, NetworkConnectionStatus.offline);
  }

  /**
   * @see {@link NetworkStatus.hasObserver}
   */
  hasObserver(observer: Observer, type?: NetworkConnectionStatus): boolean {
    const comparator = this.comparator(observer);
    if (!type) {
      const {online, offline} = this._observers;
      return !!(
        online.find(comparator) || offline.find(comparator)
      );
    }
    return !!this._observers[type].find(comparator);
  }

  /**
   * Invoked when the application user's network getConnection goes offline.
   * @see onNetworkStatusChange
   */
  private onDisconnected = (evt: Event) => {
    this.onNetworkStatusChange(NetworkConnectionStatus.offline, evt);
  }

  /**
   * Invoked when the application user's network getConnection goes online.
   * @see onNetworkStatusChange
   */
  private onConnected = (evt: Event) => {
    this.onNetworkStatusChange(NetworkConnectionStatus.online, evt);
  }

  /**
   * Invoked when the application receives a network status changed event, either
   * online or offline, at which point all registered observers for the given event
   * are invoked and notified of the event.
   */
  private onNetworkStatusChange = (type: NetworkConnectionStatus, evt: Event) => {
    this._connected = (type === NetworkConnectionStatus.online);
    this._observers[type].forEach((observer: Observer) => {
      observer(this._connected, evt);
    });
  }

  /**
   * Provides a utility from which a network status change event observer is added.
   */
  private addObserver(observer: Observer, type: NetworkConnectionStatus): boolean {
    if (!this.hasObserver(observer, type)) {
      this._observers[type].push(observer);
      return true;
    }
    return false;
  }

  /**
   * Provides a utility from which a network status change event observer is removed.
   */
  private removeObserver(observer: Observer, type: NetworkConnectionStatus) {
    if (this.hasObserver(observer, type)) {
      const observers: Observer[] = this._observers[type];
      this._observers[type] = observers.filter(this.comparator(observer, true));
      return true;
    }
    return false;
  }

  /**
   * Provides a utility method which returns a comparator function for use in determining
   * if the provided observer is a reference to an existing observer.
   */
  private comparator(observer: Observer, inverse = false): Comparator {
    return function(handler: Observer): boolean {
      if (inverse) {
        return handler !== observer;
      }
      return handler === observer;
    };
  }

  /**
   * @see {@link NetworkStatus.destruct}
   */
  destruct(): void {
    window.removeEventListener(NetworkConnectionStatus.offline, this.onDisconnected);
    window.removeEventListener(NetworkConnectionStatus.online, this.onConnected);
    connection._observers = null;
    connection = null;
  }
};

/**
 * Defines a module level reference to the `ConnectionStatus` instance.
 */
let connection: ConnectionStatus;

/**
 * @see {@link NetworkStatus}
 */
const getConnection = (onDisconnect?: Observer, onConnect?: Observer, observeAll = false): ConnectionStatus => {
  if (!connection) {
    connection = new ConnectionStatus();
  }
  connection.addObservers(onDisconnect, onConnect, observeAll);
  return connection;
};

/**
 * @see {@link NetworkStatus}
 */
export default getConnection;
